/**
 * @file src/_modulos/prenota/tabela/layouts/config/api/builder.ts
 * @description Construtor simplificado de payload para a API
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas constrói payload
 * - YAGNI: Remove funcionalidades não utilizadas
 */

import { useUserStore } from "store";
import { FILTER_FLAGS, QUICK_FILTER_FIELDS, type AppliedFilters } from "@PN/tabela";
import { fmt } from "@/_nucleo/visuais";

/* -------------------------------------------------------------------------- */
/* CONSTRUTOR DE FILTROS                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Constrói filtros baseados na tab ativa
 */
export function buildTabFilters(
  tab: string,
  userLogin: string
): AppliedFilters {
  switch (tab) {
    case "minhas":
      return { F1_XUSRRA: userLogin };
    case "pendentes":
      return {
        [QUICK_FILTER_FIELDS.STATUS]: ["Pendente"],
        F1_XUSRRA: userLogin,
      };
    case "revisao":
      return {
        [QUICK_FILTER_FIELDS.STATUS]: ["Revisar"],
        F1_XUSRRA: userLogin,
      };
    default:
      return {};
  }
}

/**
 * Constrói filtros combinados (aplicados + tab)
 */
export function buildCombinedFilters(
  appliedFilters: AppliedFilters,
  tab: string,
  userLogin: string
): AppliedFilters {
  const tabFilters = buildTabFilters(tab, userLogin);
  return { ...tabFilters, ...appliedFilters };
}

/**
 * Constrói o payload para a API
 */
export function buildApiPayload(
  appliedFilters: AppliedFilters,
  tab: string,
  filials: string[],
  sorting: Array<{ id: string; desc: boolean }>,
  page: number = 1,
  pageSize: number = 10,
  searchTerm?: string,
  searchField?: string
) {
  const storeState = useUserStore.getState();
  const userLogin = storeState.user?.login ?? "";
  const userGroups = storeState.grupos?.map((g) => g.codigo) ?? [];
  const combinedFilters = buildCombinedFilters(appliedFilters, tab, userLogin);
  const apiFilters = convertFiltersToApiFormat(combinedFilters);

  const payload = {
    page,
    pageSize,
    filiais: filials, // Corrigido: API espera 'filiais', não 'filials'
    search: searchTerm,
    searchField: searchField,
    order_by: sorting.length > 0 ? getOrderByField(sorting[0].id) : "Z05_DATA",
    order_dir: (sorting.length > 0
      ? sorting[0].desc
        ? "DESC"
        : "ASC"
      : "DESC") as "DESC" | "ASC",
    userGroups,
    userLogin,
    ...apiFilters,
  };

  // Debug: log do payload construído
  console.log("🔧 [buildApiPayload] Payload construído:", {
    appliedFilters,
    tab,
    userLogin,
    combinedFilters,
    apiFilters,
    finalPayload: payload,
  });

  return payload;
}

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Mapeia IDs das colunas para campos da API
 */
function getOrderByField(columnId: string): string {
  const fieldMapping: Record<string, string> = {
    F1_XUSRRA: "F1_XUSRRA",
    F1_XTIPO: "F1_XTIPO",
    F1_DOC: "F1_DOC",
    A2_NOME: "A2_NOME",
    Z05_DATA: "Z05_DATA",
    VENCIMENTO: "Z10_VENCTO", // Corrigido: VENCIMENTO mapeia para Z10_VENCTO
    F1_VALBRUT: "F1_VALBRUT",
    F1_XPRIOR: "F1_XPRIOR",
    Status: "Status",
    F1_XORI: "F1_XORI",
    F1_EMISSAO: "F1_EMISSAO",
    F1_SERIE: "F1_SERIE",
    F1_DTDIGIT: "F1_DTDIGIT",
    Z05_HORA: "Z05_HORA",
  };
  return fieldMapping[columnId] || "Z05_DATA";
}

/**
 * Converte filtros para o formato da API
 */
function convertFiltersToApiFormat(filters: Record<string, any>) {
  const apiFilters: Record<string, any> = {};
  const fieldMapping: Record<string, string> = {
    F1_DOC: "filtro_doc",
    F1_COND: "filtro_cond",
    A2_NOME: "filtro_fornece",
    F1_XPRIOR: "filtro_prioridade",
    F1_XUSRRA: "filtro_usuario",
    F1_FILIAL: "filtro_filial",
    F1_XTIPO: "filtro_tipo",
    Status: "filtro_status",
    F1_XORI: "filtro_observacoes",
    [FILTER_FLAGS.SEM_BOLETO]: "filtro_sem_boleto",
  };

  console.log("🔄 [convertFiltersToApiFormat] Filtros de entrada:", filters);

  for (const [field, value] of Object.entries(filters)) {
    // --- Tratamento específico para range numérico F1_VALBRUT ---
    if (field === "F1_VALBRUT") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        apiFilters.filtro_valor_de = value.min ?? value.minValue ?? undefined;
        apiFilters.filtro_valor_ate = value.max ?? value.maxValue ?? undefined;
        continue;
      }
    
      if (typeof value === "string") {
        const parts = value.split(/[-;—–]/).map((p) => p.trim()).filter(Boolean);
        apiFilters.filtro_valor_de = parts[0] || undefined;
        apiFilters.filtro_valor_ate = parts[1] || undefined;
        continue;
      }
    }
    

    // --- Campos mapeados normalmente ---
    const apiField = fieldMapping[field];
    if (apiField) {
      if (field === FILTER_FLAGS.SEM_BOLETO) {
        apiFilters[apiField] = value === "1" || value === 1 || value === true;
        continue;
      }
      if (field === "F1_FILIAL" || field === "Status" || field === "F1_XTIPO") {
        apiFilters[apiField] = Array.isArray(value) ? value : [value];
      } else {
        apiFilters[apiField] = Array.isArray(value)
          ? value.length > 0
            ? value[0]
            : undefined
          : value;
      }
    }

    // --- Datas (mantém seu comportamento atual, aceita object.from/to) ---
    if (field === "Z05_DATA") {
      const parseDate = (s: string) => {
        const [d, m, y] = s.split("/").map(Number);
        return new Date(y, m - 1, d);
      };
    
      if (typeof value === "object" && value.from) {
        apiFilters.filtro_data_de = fmt.dates.YYYYMMDD(parseDate(value.from));
      }
      if (typeof value === "object" && value.to) {
        apiFilters.filtro_data_ate = fmt.dates.YYYYMMDD(parseDate(value.to));
      }
      if (typeof value === "string" && value.includes("-")) {
        const [from, to] = value.split("-").map((s) => s.trim());
        if (from) apiFilters.filtro_data_de = fmt.dates.YYYYMMDD(parseDate(from));
        if (to) apiFilters.filtro_data_ate = fmt.dates.YYYYMMDD(parseDate(to));
      }
    }

    if (field === "F1_EMISSAO") {
      const parseDate = (s: string) => {
        const [d, m, y] = s.split("/").map(Number);
        return new Date(y, m - 1, d);
      };
    
      if (typeof value === "object" && value.from) {
        apiFilters.filtro_emissao_de = fmt.dates.YYYYMMDD(parseDate(value.from));
      }
      if (typeof value === "object" && value.to) {
        apiFilters.filtro_emissao_ate = fmt.dates.YYYYMMDD(parseDate(value.to));
      }
      if (typeof value === "string" && value.includes("-")) {
        const [from, to] = value.split("-").map((s) => s.trim());
        if (from) apiFilters.filtro_emissao_de = fmt.dates.YYYYMMDD(parseDate(from));
        if (to) apiFilters.filtro_emissao_ate = fmt.dates.YYYYMMDD(parseDate(to));
      }
    }

    if (field === "VENCIMENTO") {
      const parseDate = (s: string) => {
        const [d, m, y] = s.split("/").map(Number);
        return new Date(y, m - 1, d);
      };
    
      if (typeof value === "object" && value.from) {
        apiFilters.filtro_vencimento_de = fmt.dates.YYYYMMDD(parseDate(value.from));
      }
      if (typeof value === "object" && value.to) {
        apiFilters.filtro_vencimento_ate = fmt.dates.YYYYMMDD(parseDate(value.to));
      }
      if (typeof value === "string" && value.includes("-")) {
        const [from, to] = value.split("-").map((s) => s.trim());
        if (from) apiFilters.filtro_vencimento_de = fmt.dates.YYYYMMDD(parseDate(from));
        if (to) apiFilters.filtro_vencimento_ate = fmt.dates.YYYYMMDD(parseDate(to));
      }
    }    
  }

  console.log("✅ [convertFiltersToApiFormat] Filtros convertidos:", apiFilters);
  return apiFilters;
}

